import { PromptPhoneBanner, useShowPromptPhoneNumber } from 'app/components/prompt-phone-number-banner';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { FormError } from 'app/design/primitives/form-error';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { RemoteError } from 'app/design/primitives/remote-error';

import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation } from 'app/hooks/navigation';
import { useAllAddresses } from 'app/network/address';
import { useMyPrivateProfile, useUpsertMyProfile } from 'app/network/profile';
import { useUser } from 'app/network/user';
import type { UpdateProfileDTO } from 'app/types/profile';
import { getSchema, type ProfileInformationFormValues } from 'app/screens/profile/my-information/schema';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl } from 'react-native';
import { AccountInformationSection } from './my-information/account-information-section';
import { AvatarSection } from './my-information/avatar-section';
import { PersonalInformationSection } from './my-information/personal-information-section';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export function MyInformations() {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { t: tInfo } = useTranslation('my-information');
  const { t: tPersonal } = useTranslation('my-personal-information');

  const { data: user, refetch: userRefetch, isRefetching: isUserRefetching } = useUser();
  const profileQuery = useMyPrivateProfile();
  const navigation = useNavigation();
  const addressesQuery = useAllAddresses();

  const { isSmallUI } = useUISize();

  const form = useForm<ProfileInformationFormValues>({
    defaultValues: getProfileFormDefaults(profileQuery, user),
    schema: getSchema(t),
  });
  const { getValues, reset: resetForm, trigger: triggerForm } = form;
  const isSaveDisabled = !form.formState.isDirty || !form.formState.isValid;

  const fieldLabels: Record<string, string> = {
    additionalName: tPersonal('additionalNameFieldLabel'),
    firstName: tInfo('firstNameFieldLabel'),
    lastName: tInfo('lastNameFieldLabel'),
    phone: tInfo('phoneFieldLabel'),
    email: tInfo('emailFieldLabel'),
    bio: tPersonal('bioFieldLabel'),
  };

  const [formError, setFormError] = useState<Error>();
  const updateProfile = useUpsertMyProfile({
    onSuccess: () => isSmallUI && navigation.goBack(),
    onError: (error) => setFormError(error),
  });

  useEffect(() => {
    if (profileQuery.data) {
      resetForm(
        {
          additionalName: profileQuery.data.additionalName ?? '',
          firstName: profileQuery.data.firstName,
          lastName: profileQuery.data.lastName,
          // A profile hydrated by an older/partial BFF response may not contain
          // the private phone field. Do not erase a value already in the form.
          phone: profileQuery.data.phone ?? getValues('phone') ?? '',
          bio: profileQuery.data.bio ?? '',
          email: user?.email ?? '',
        },
        { keepDirtyValues: true },
      );
      void triggerForm();
    }
  }, [getValues, profileQuery.data, resetForm, triggerForm, user?.email]);

  const onSubmit = form.handleSubmit((f: UpdateProfileDTO) => {
    setFormError(undefined);
    updateProfile.mutate(f);
  });

  const defaultPadding = usePagePadding();
  const pagePaddingStyle = { ...defaultPadding, paddingLeft: 0, paddingRight: 0 };

  const showPrompt = useShowPromptPhoneNumber();

  useRegisterLoading(profileQuery.isPending, 'my-information-profile');
  useRegisterLoading(addressesQuery.isPending, 'my-information-addresses');

  return (
    <Page
      accessory={
        <ProfileSaveAccessory
          form={form}
          fieldLabels={fieldLabels}
          formError={formError}
          tAPI={tAPI}
          isSaveDisabled={isSaveDisabled}
          updateProfile={updateProfile}
          onSubmit={onSubmit}
          t={t}
        />
      }
      paddingStyle={pagePaddingStyle}
      refreshControl={
        <RefreshControl
          onRefresh={() => void Promise.all([userRefetch(), profileQuery.refetch(), addressesQuery.refetch()])}
          refreshing={
            isUserRefetching ||
            profileQuery.isRefetching ||
            (addressesQuery.isRefetching && addressesQuery.data === undefined)
          }
        />
      }
    >
      <Column gap="spacing.general.x4" style={{ backgroundColor: theme.colors['surface.default'] }}>
        {isSmallUI ? <AvatarSection profile={profileQuery.data} /> : null}

        {showPrompt ? (
          <Row paddingHorizontal="spacing.general.x4" justifyContent="center" alignItems="center">
            <PromptPhoneBanner fullWidth />
          </Row>
        ) : null}

        <PersonalInformationSection control={form.control} profile={profileQuery.data} />
        <AccountInformationSection control={form.control} profile={profileQuery.data} />
      </Column>
    </Page>
  );
}

function getProfileFormDefaults(
  profileQuery: ReturnType<typeof useMyPrivateProfile>,
  user: ReturnType<typeof useUser>['data'],
): ProfileInformationFormValues {
  return {
    additionalName: profileQuery.data?.additionalName ?? '',
    firstName: profileQuery.data?.firstName ?? '',
    lastName: profileQuery.data?.lastName ?? '',
    phone: profileQuery.data?.phone ?? '',
    bio: profileQuery.data?.bio ?? '',
    email: user?.email ?? '',
  };
}

function ProfileSaveAccessory({
  form,
  fieldLabels,
  formError,
  tAPI,
  isSaveDisabled,
  updateProfile,
  onSubmit,
  t,
}: {
  form: ReturnType<typeof useForm<ProfileInformationFormValues>>;
  fieldLabels: Record<string, string>;
  formError: Error | undefined;
  tAPI: ReturnType<typeof useTranslation<'api-errors'>>['t'];
  isSaveDisabled: boolean;
  updateProfile: ReturnType<typeof useUpsertMyProfile>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  return (
    <Accessory marginBottom={theme.spacing['spacing.general.x4']} marginTop={theme.spacing['spacing.general.x4']}>
      <Column gap="spacing.general.x2" justifyContent="center" paddingHorizontal="spacing.general.x4">
        {!form.formState.isDirty && Object.keys(form.formState.errors).length > 0 && (
          <Column gap="spacing.general.x1">
            {Object.entries(form.formState.errors).map(([field, error]) => (
              <FormError key={field} errorMessage={`${fieldLabels[field] ?? field} : ${error.message ?? ''}`} />
            ))}
          </Column>
        )}
        {formError ? <RemoteError {...{ error: formError, message: tAPI(formError.message) }} /> : null}

        <Button
          disabled={isSaveDisabled}
          loading={updateProfile.isPending}
          onPress={() => void onSubmit()}
          variant="primary"
        >
          {t('primaryButtonText', { ns: 'my-personal-information' })}
        </Button>
      </Column>
    </Accessory>
  );
}
